import { clearCaptureTransfers, sweepCaptureTransfers } from '@/lib/incidents/capture-transfer';
/** WHS drafts hold health narratives. They live in this browser tab only (sessionStorage), so a
 * closed tab, a signed-out session or a shared workstation does not keep them. Earlier versions
 * used localStorage; those copies are removed, never migrated or read. */
export const WHS_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const writtenAtByKey = new Map<string, number>();
const isWhsDraft = (key: string) =>
  key.startsWith('whs-report-v1:') || key.startsWith('whs-edit-v1:');

export function readWhsDraft<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { writtenAt?: number; draft?: T };
    const age = Date.now() - Number(saved.writtenAt);
    if (!Number.isFinite(age) || age < 0 || age >= WHS_DRAFT_MAX_AGE_MS || !saved.draft) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    writtenAtByKey.set(key, Number(saved.writtenAt));
    return saved.draft;
  } catch {
    removeWhsDraft(key);
    return null;
  }
}
export function writeWhsDraft(key: string, draft: unknown): boolean {
  try {
    const raw = window.sessionStorage.getItem(key);
    const previous = raw ? (JSON.parse(raw) as { writtenAt?: number }) : null;
    const writtenAt = writtenAtByKey.get(key) ?? previous?.writtenAt ?? Date.now();
    writtenAtByKey.set(key, writtenAt);
    const age = Date.now() - writtenAt;
    if (!Number.isFinite(age) || age < 0 || age >= WHS_DRAFT_MAX_AGE_MS) {
      window.sessionStorage.removeItem(key);
      return false;
    }
    window.sessionStorage.setItem(key, JSON.stringify({ writtenAt, draft }));
    return true;
  } catch {
    return false;
  }
}
export function removeWhsDraft(key: string): boolean {
  writtenAtByKey.delete(key);
  try {
    window.sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}
/** Remove drafts an earlier version left in persistent browser storage. */
export function purgeLegacyLocalWhsDrafts(): void {
  try {
    const storage = window.localStorage;
    const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
    for (const key of keys) if (key && isWhsDraft(key)) storage.removeItem(key);
  } catch {
    /* Storage denial must not block the app shell or sign-out. */
  }
}
export function clearWhsDrafts(): void {
  writtenAtByKey.clear();
  try {
    clearCaptureTransfers(window.sessionStorage);
  } catch {
    /* Storage denial must not prevent sign-out. */
  }
  purgeLegacyLocalWhsDrafts();
  try {
    const storage = window.sessionStorage;
    const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
    for (const key of keys) if (key && isWhsDraft(key)) removeWhsDraft(key);
  } catch {
    /* Storage denial must not prevent sign-out. */
  }
}
/** Normal sign-out is a native POST: clear before navigation, including from other pages. */
export function installWhsDraftSignOutCleanup(): () => void {
  const onSubmit = (event: Event) => {
    const form = event.target;
    if (
      form instanceof HTMLFormElement &&
      new URL(form.action, window.location.href).pathname === '/auth/signout'
    )
      clearWhsDrafts();
  };
  document.addEventListener('submit', onSubmit, true);
  return () => document.removeEventListener('submit', onSubmit, true);
}

/** Sweep every account/provider key, even when the incident page is never revisited. */
export function sweepWhsDrafts(): void {
  purgeLegacyLocalWhsDrafts();
  try {
    sweepCaptureTransfers(window.sessionStorage);
  } catch {
    /* Storage denial must not block the app shell. */
  }
  try {
    const storage = window.sessionStorage;
    const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
    for (const key of keys) {
      if (!key || !isWhsDraft(key)) continue;
      try {
        const saved = JSON.parse(storage.getItem(key) ?? 'null') as {
          writtenAt?: number;
          draft?: unknown;
        } | null;
        const age = Date.now() - Number(saved?.writtenAt);
        if (!saved?.draft || !Number.isFinite(age) || age < 0 || age >= WHS_DRAFT_MAX_AGE_MS)
          storage.removeItem(key);
      } catch {
        storage.removeItem(key);
      }
    }
  } catch {
    /* Storage denial must not block the app shell. */
  }
}
export function installWhsDraftExpirySweep(): () => void {
  sweepWhsDrafts();
  const onVisible = () => {
    if (document.visibilityState === 'visible') sweepWhsDrafts();
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => document.removeEventListener('visibilitychange', onVisible);
}
